import { FormPreview } from '@/components/templates-detail'
import { TemplateGuide } from '@/components/templates-detail/guide'
import { RelatedTemplates } from '@/components/templates-detail/related'
import { RiArrowRightLine, RiArrowRightSLine } from '@remixicon/react'
import { TemplateService, TemplatesResult } from '@/service'
import { Container } from '@/components'
import Link from 'next/link'
import { libreBaskerville as editorial } from '@/fonts'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { getHeadings, Heading } from '@/utils/toc'
import { slugifyText } from '@/utils/helper'
import styles from '@/components/templates-detail/detail.module.css'
import { breadcrumbSchema } from '@/utils/seo'

interface TemplatesDetailPageProps {
  template: TemplatesResult
  related: TemplatesResult[]
  headings: Heading[]
}
const HOMEPAGE_URI = process.env.NEXT_PUBLIC_HOMEPAGE_URI

const TemplatesDetailPage = ({
  template,
  related
}: TemplatesDetailPageProps): JSX.Element => {
  const normalizedTemplateSlug = template.Slug.toLowerCase()
  const useTemplateUrl = `${HOMEPAGE_URI}/template/${encodeURIComponent(template.templateId)}?recordId=${encodeURIComponent(template.id)}`
  return (
    <Container
      headerBackground="#fff"
      url={`/templates/${encodeURIComponent(normalizedTemplateSlug)}`}
      schema={[
        breadcrumbSchema([
          { name: 'Templates', path: '/templates' },
          ...(template.Category?.[0]
            ? [
                {
                  name: template.Category[0],
                  path: `/templates/category/${encodeURIComponent(slugifyText(template.Category[0]))}`
                }
              ]
            : []),
          {
            name: template.Title,
            path: `/templates/${encodeURIComponent(normalizedTemplateSlug)}`
          }
        ])
      ]}
      title={`Free ${template.Title} | HeyForm`}
      description={
        template.Intro ||
        `Explore free ${template.Title} and customize it to fit your needs with HeyForm.`
      }
    >
      <main className={styles.page}>
        <div className={styles.container}>
          <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
            <Link href="/templates">Templates</Link>
            {template.Category?.[0] && (
              <>
                <RiArrowRightSLine aria-hidden="true" />
                <Link
                  href={`/templates/category/${encodeURIComponent(slugifyText(template.Category[0]))}`}
                >
                  {template.Category[0]}
                </Link>
              </>
            )}
          </nav>
          <section className={styles.hero} aria-labelledby="template-title">
            <div className={styles.heroCopy}>
              <h1 id="template-title" className={editorial.className}>
                {template.Title}
              </h1>
              {template.Intro && (
                <p className={styles.intro}>{template.Intro}</p>
              )}
              <a className={styles.button} href={useTemplateUrl}>
                Use this template
                <RiArrowRightLine aria-hidden="true" />
              </a>
              <div className={styles.facts}>
                {template.Questions > 0 && (
                  <span>
                    {template.Questions}{' '}
                    {template.Questions === 1 ? 'question' : 'questions'}
                  </span>
                )}
                <span>Fully customizable</span>
              </div>
            </div>
            <FormPreview key={template.id} template={template} />
          </section>
          {template.Content && (
            <TemplateGuide key={template.id} html={template.Content} />
          )}
          {related.length > 0 && (
            <RelatedTemplates
              templates={related}
              headingClassName={editorial.className}
            />
          )}
          <section
            className={styles.customization}
            aria-labelledby="template-customize-title"
          >
            <div className={styles.customizationCopy}>
              <h2 id="template-customize-title" className={editorial.className}>
                A starting point.
                <br />
                Your own finish.
              </h2>
              <p>
                Keep the structure you need. Change the questions, colors, and
                flow to make this template feel like you.
              </p>
              <Link className={styles.textLink} href="/product#customization">
                Explore what you can customize
                <RiArrowRightLine aria-hidden="true" />
              </Link>
            </div>
            <img
              src="/static/customization.webp"
              alt="HeyForm design controls beside a customized customer feedback form"
              width={2000}
              height={1600}
              loading="lazy"
            />
          </section>
          <section
            className={styles.closing}
            aria-labelledby="template-closing-title"
          >
            <h2 id="template-closing-title" className={editorial.className}>
              Make it yours.
            </h2>
            <a className={styles.button} href={useTemplateUrl}>
              Use this template
              <RiArrowRightLine aria-hidden="true" />
            </a>
          </section>
        </div>
      </main>
    </Container>
  )
}

export async function getServerSideProps({
  params
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<TemplatesDetailPageProps>
> {
  const rawSlug = params.slug as string
  const slug = rawSlug.toLowerCase()
  const result = await TemplateService.list()

  if (rawSlug !== slug) {
    return {
      redirect: {
        destination: `/templates/${encodeURIComponent(slug)}`,
        permanent: true
      }
    }
  }

  const template = result.find(t => t.Slug.toLowerCase() === slug)
  let headings: Heading[] = []

  if (!template) {
    return {
      notFound: true
    }
  }

  if (template.Article) {
    const article = await TemplateService.article(template.Article)

    if (article) {
      template.Content = article.html
      headings = getHeadings(article.html)
    }
  }

  const related = TemplateService.random(
    result,
    template.Category[0],
    template.id,
    6
  )

  return {
    props: {
      template,
      headings,
      related
    }
  }
}

export default TemplatesDetailPage
